import React, { useEffect } from 'react'
import { DatePicker, Flex, Select } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { countries } from '../../../../../libs/countries'
import { useStore } from 'effector-react'
import { $generateBalanceFilter, setGeneralBalanceFilter } from './model'
import { changedDateFormat } from '../../../../../libs/utils'
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subMonths,
  subQuarters,
  subWeeks,
} from 'date-fns'
import { useUrlFilters } from '../../../../../libs/useUrlFilters'

dayjs.extend(customParseFormat)

const FILTER_CONFIG = {
  countries: { type: 'array' as const },
  dateFilter: { type: 'string' as const, default: 'month' },
  dateFrom: { type: 'string' as const },
  dateTo: { type: 'string' as const },
}

export const GeneralBalanceFilter: React.FC = () => {
  const generateBalanceFilter = useStore($generateBalanceFilter)
  const { filters, setFilter, setFilters } = useUrlFilters(FILTER_CONFIG)

  const localDataFilter = filters.dateFilter ?? 'month'

  useEffect(() => {
    const urlCountries = filters.countries ?? []

    if (localDataFilter === 'date') {
      if (filters.dateFrom && filters.dateTo) {
        setGeneralBalanceFilter({
          countries: urlCountries,
          date: { dateFrom: filters.dateFrom, dateTo: filters.dateTo },
        })
      }
      return
    }

    if (localDataFilter === 'week') {
      const today = new Date()
      const dayOfWeek = today.getDay()
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const startOfWeekDate = new Date(today)
      startOfWeekDate.setDate(today.getDate() - diffToMonday)

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: {
          dateFrom: changedDateFormat(startOfWeekDate),
          dateTo: changedDateFormat(today),
        },
      })
    } else if (localDataFilter === 'month') {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      const firstDay = changedDateFormat(firstDayOfMonth)
      const endOfMonthDate = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0,
      )
      const lastDay = changedDateFormat(
        new Date(Math.min(endOfMonthDate.getTime(), today.getTime())),
      )

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: { dateFrom: firstDay, dateTo: lastDay },
      })
    } else if (localDataFilter === 'lastWeek') {
      const today = new Date()
      const prevWeekStart = changedDateFormat(
        startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      )
      const prevWeekEnd = changedDateFormat(
        endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      )

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: { dateFrom: prevWeekStart, dateTo: prevWeekEnd },
      })
    } else if (localDataFilter === 'lastMonth') {
      const today = new Date()
      const prevMonthStart = changedDateFormat(
        startOfMonth(subMonths(today, 1)),
      )
      const prevMonthEnd = changedDateFormat(endOfMonth(subMonths(today, 1)))

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: { dateFrom: prevMonthStart, dateTo: prevMonthEnd },
      })
    } else if (localDataFilter === 'lastQuarter') {
      const today = new Date()
      const prevQuarterStart = changedDateFormat(
        startOfQuarter(subQuarters(today, 1)),
      )
      const prevQuarterEnd = changedDateFormat(
        endOfQuarter(subQuarters(today, 1)),
      )

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: { dateFrom: prevQuarterStart, dateTo: prevQuarterEnd },
      })
    } else if (localDataFilter === 'thisQuarter') {
      const today = new Date()
      const quarterStart = changedDateFormat(startOfQuarter(today))

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: { dateFrom: quarterStart, dateTo: changedDateFormat(today) },
      })
    } else if (dayjs(localDataFilter, 'DD.MM.YYYY', true).isValid()) {
      const today = new Date()
      const fixedStart = changedDateFormat(
        dayjs(localDataFilter, 'DD.MM.YYYY').toDate(),
      )

      setGeneralBalanceFilter({
        countries: urlCountries,
        date: { dateFrom: fixedStart, dateTo: changedDateFormat(today) },
      })
    }
  }, [localDataFilter, filters.dateFrom, filters.dateTo])

  return (
    <Flex gap={16} style={{ height: 'auto' }}>
      <Flex gap={16} style={{ width: '40%' }}>
        <Select
          mode="multiple"
          placeholder="Countries"
          size="large"
          style={{ width: '100%' }}
          maxTagCount="responsive"
          value={filters.countries}
          options={countries.map((c) => ({ value: c.value, label: c.text }))}
          onChange={(values: string[]) => {
            setFilter('countries', values)
            setGeneralBalanceFilter({
              ...generateBalanceFilter,
              countries: values,
            })
          }}
        />
        <Select
          value={localDataFilter}
          style={{ width: '100%' }}
          size="large"
          onChange={(value: string) => {
            setFilters({
              dateFilter: value,
              ...(value !== 'date'
                ? { dateFrom: undefined, dateTo: undefined }
                : {}),
            })
          }}
          options={[
            { value: 'date', label: 'Date' },
            { value: '01.06.2025', label: 'From 01.06.2025' },
            { value: 'week', label: 'This week' },
            { value: 'month', label: 'This month' },
            { value: 'thisQuarter', label: 'This quarter' },
            { value: 'lastWeek', label: 'Last week' },
            { value: 'lastMonth', label: 'Last month' },
            { value: 'lastQuarter', label: 'Last quarter' },
          ]}
        />
      </Flex>

      {localDataFilter === 'date' && (
        <DatePicker.RangePicker
          size="large"
          value={
            filters.dateFrom && filters.dateTo
              ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
              : undefined
          }
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              const dateFrom = changedDateFormat(dates[0].toDate())
              const dateTo = changedDateFormat(dates[1].toDate())
              setFilters({ dateFrom, dateTo })
              setGeneralBalanceFilter({
                ...generateBalanceFilter,
                date: { dateFrom, dateTo },
              })
            } else {
              setFilters({ dateFrom: undefined, dateTo: undefined })
            }
          }}
        />
      )}
    </Flex>
  )
}
